import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ArrowLeft, ChevronRight, BarChart3, Trophy } from 'lucide-react';
import { API } from './constants';

export const StatsView = ({ stats, recentScores, history, onBack, onViewRound }) => {
  const dist = stats?.score_distribution || {};
  const totalDist = (dist.eagle_or_better || 0) + (dist.birdie || 0) + (dist.par || 0) + (dist.bogey || 0) + (dist.double_plus || 0);
  const distPct = (val) => totalDist > 0 ? Math.round((val / totalDist) * 100) : 0;
  const [clubProfile, setClubProfile] = useState([]);
  const [showBag, setShowBag] = useState(false);
  const [strokesGained, setStrokesGained] = useState(null);
  const [showSG, setShowSG] = useState(false);

  useEffect(() => {
    if (!showBag) return;
    const fetchClubs = async () => {
      try {
        const res = await axios.get(`${API}/golf/club-distances`);
        setClubProfile(res.data.profile || []);
      } catch { setClubProfile([]); }
    };
    fetchClubs();
  }, [showBag]);

  useEffect(() => {
    if (!showSG) return;
    const fetchSG = async () => {
      try {
        const res = await axios.get(`${API}/golf/strokes-gained`);
        setStrokesGained(res.data);
      } catch { setStrokesGained(null); }
    };
    fetchSG();
  }, [showSG]);

  return (
    <div className="space-y-4" data-testid="stats-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="stats-back"><ArrowLeft className="w-5 h-5" /></Button>
        <h2 className="text-lg font-bold text-foreground">My Stats</h2>
      </div>
      {!stats ? (
        <div className="text-center py-12"><BarChart3 className="w-16 h-16 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">Complete a round to see your stats</p></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Card className="border-primary/30 bg-primary/5"><CardContent className="p-3 text-center"><div className="text-2xl font-bold text-foreground">{stats.scoring_avg}</div><div className="text-xs text-muted-foreground">Scoring Avg</div></CardContent></Card>
            <Card className="border-green-500/30 bg-green-500/5"><CardContent className="p-3 text-center"><div className="text-2xl font-bold text-foreground">{stats.best_score}</div><div className="text-xs text-muted-foreground">Best Score</div></CardContent></Card>
            <Card><CardContent className="p-3 text-center"><div className="text-xl font-bold text-foreground">{stats.avg_putts}</div><div className="text-xs text-muted-foreground">Avg Putts</div></CardContent></Card>
            <Card><CardContent className="p-3 text-center"><div className="text-xl font-bold text-foreground">{stats.avg_gir_pct}%</div><div className="text-xs text-muted-foreground">Avg GIR</div></CardContent></Card>
            <Card><CardContent className="p-3 text-center"><div className="text-xl font-bold text-foreground">{stats.avg_fairway_pct}%</div><div className="text-xs text-muted-foreground">Avg Fairways</div></CardContent></Card>
            <Card><CardContent className="p-3 text-center"><div className="text-xl font-bold text-foreground">{stats.avg_to_par > 0 ? '+' : ''}{stats.avg_to_par}</div><div className="text-xs text-muted-foreground">Avg to Par</div></CardContent></Card>
          </div>
          {totalDist > 0 && (
            <div className="bg-card rounded-xl border border-border/50 p-4" data-testid="score-distribution">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4">Score Distribution</h3>
              <div className="space-y-3">
                {[
                  { label: 'Eagle+', value: dist.eagle_or_better || 0, color: 'bg-yellow-500' },
                  { label: 'Birdie', value: dist.birdie || 0, color: 'bg-red-500' },
                  { label: 'Par', value: dist.par || 0, color: 'bg-green-500' },
                  { label: 'Bogey', value: dist.bogey || 0, color: 'bg-blue-500' },
                  { label: 'Double+', value: dist.double_plus || 0, color: 'bg-blue-700' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-muted-foreground w-14">{item.label}</span>
                    <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${item.color} rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                        style={{ width: `${Math.max(distPct(item.value), item.value > 0 ? 8 : 0)}%` }}>
                        {item.value > 0 && <span className="text-[10px] font-bold text-white">{item.value}</span>}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground w-10 text-right">{distPct(item.value)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(stats.par3_avg !== null || stats.par4_avg !== null || stats.par5_avg !== null) && (
            <div className="bg-card rounded-xl border border-border/50 p-4" data-testid="par-performance">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Avg to Par by Hole Type</h3>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Par 3', value: stats.par3_avg },
                  { label: 'Par 4', value: stats.par4_avg },
                  { label: 'Par 5', value: stats.par5_avg },
                ].map(item => (
                  <div key={item.label} className="text-center p-3 rounded-xl bg-muted/40">
                    <div className={`text-xl font-bold ${item.value === null ? 'text-muted-foreground' : item.value < 0 ? 'text-red-500' : item.value === 0 ? 'text-green-500' : 'text-blue-500'}`}>
                      {item.value === null ? '-' : item.value > 0 ? `+${item.value}` : item.value === 0 ? 'E' : item.value}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <Card><CardContent className="p-3 text-center"><div className="text-lg font-bold text-foreground">{stats.total_holes_played || 0}</div><div className="text-[10px] text-muted-foreground">Holes Played</div></CardContent></Card>
            <Card><CardContent className="p-3 text-center"><div className="text-lg font-bold text-foreground">{stats.total_penalties || 0}</div><div className="text-[10px] text-muted-foreground">Total Penalties</div></CardContent></Card>
            <Card><CardContent className="p-3 text-center"><div className="text-lg font-bold text-foreground">{stats.total_bunkers || 0}</div><div className="text-[10px] text-muted-foreground">Total Bunkers</div></CardContent></Card>
          </div>
          {recentScores && recentScores.length > 1 && (
            <div className="bg-card rounded-xl border border-border/50 p-4" data-testid="scoring-trend">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Scoring Trend (Last {recentScores.length} Rounds)</h3>
              <div className="flex items-end gap-1 h-24">
                {recentScores.slice().reverse().map((tp, i) => {
                  const maxVal = Math.max(...recentScores.map(Math.abs), 5);
                  const height = Math.max(Math.abs(tp) / maxVal * 100, 10);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                      <div className="text-[8px] font-bold mb-0.5">{tp > 0 ? `+${tp}` : tp === 0 ? 'E' : tp}</div>
                      <div className={`w-full rounded-t-md transition-all ${tp < 0 ? 'bg-red-500' : tp === 0 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ height: `${height}%`, minHeight: '4px' }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[8px] text-muted-foreground mt-1"><span>Oldest</span><span>Recent</span></div>
            </div>
          )}
          {stats.best_round && (
            <Card className="border-yellow-500/30 bg-yellow-500/5">
              <CardContent className="p-3 flex items-center gap-3">
                <Trophy className="w-8 h-8 text-yellow-500" />
                <div><div className="text-sm font-semibold">Best Round: {stats.best_round.score}</div><div className="text-xs text-muted-foreground">{stats.best_round.course} · {stats.best_round.date ? new Date(stats.best_round.date).toLocaleDateString() : ''}</div></div>
              </CardContent>
            </Card>
          )}
          {/* My Bag: Club Distance Profile */}
          <div className="bg-card rounded-xl border border-border/50 overflow-hidden" data-testid="my-bag-section">
            <button
              onClick={() => setShowBag(!showBag)}
              className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
              data-testid="toggle-my-bag"
            >
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">My Bag — Club Distances</h3>
              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showBag ? 'rotate-90' : ''}`} />
            </button>
            {showBag && (
              <div className="px-4 pb-4">
                {clubProfile.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <p className="text-sm font-medium">No club data yet</p>
                    <p className="text-xs mt-1">Use the Shot Tracker in GPS mode during a round to build your personal club distances.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {clubProfile.map(c => {
                      const maxAvg = Math.max(...clubProfile.map(p => p.avg), 1);
                      const barWidth = Math.round((c.avg / maxAvg) * 100);
                      return (
                        <div key={c.club} className="flex items-center gap-3" data-testid={`club-stat-${c.club}`}>
                          <span className="text-xs font-bold text-foreground w-12 text-right">{c.club}</span>
                          <div className="flex-1 h-7 bg-muted/50 rounded-lg overflow-hidden relative">
                            <div
                              className="h-full bg-gradient-to-r from-purple-500/80 to-purple-400/60 rounded-lg transition-all duration-500"
                              style={{ width: `${barWidth}%` }}
                            />
                            <div className="absolute inset-0 flex items-center px-2 justify-between">
                              <span className="text-[10px] font-bold text-foreground">{c.avg} yds</span>
                              <span className="text-[9px] text-muted-foreground">{c.min}-{c.max} · {c.count} shots</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Strokes Gained Analysis */}
          <div className="bg-card rounded-xl border border-border/50 overflow-hidden" data-testid="strokes-gained-section">
            <button
              onClick={() => setShowSG(!showSG)}
              className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
              data-testid="toggle-strokes-gained"
            >
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Strokes Gained Analysis</h3>
              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showSG ? 'rotate-90' : ''}`} />
            </button>
            {showSG && (
              <div className="px-4 pb-4">
                {!strokesGained ? (
                  <div className="text-center py-4 text-muted-foreground text-sm">Loading...</div>
                ) : (
                  <div className="space-y-4">
                    {strokesGained.message && (
                      <p className="text-[11px] text-muted-foreground bg-muted/30 rounded-lg px-3 py-2">{strokesGained.message}</p>
                    )}
                    <div className="space-y-2">
                      {[
                        { key: 'off_the_tee', label: 'Off the Tee', icon: '🏌️' },
                        { key: 'approach', label: 'Approach', icon: '🎯' },
                        { key: 'around_green', label: 'Short Game', icon: '⛳' },
                        { key: 'putting', label: 'Putting', icon: '🕳️' },
                      ].map(cat => {
                        const data = strokesGained.strokes_gained?.[cat.key];
                        if (!data || data.avg === null) return (
                          <div key={cat.key} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-muted/20">
                            <span className="text-sm w-6 text-center">{cat.icon}</span>
                            <span className="text-xs font-semibold text-muted-foreground flex-1">{cat.label}</span>
                            <span className="text-xs text-muted-foreground">No data</span>
                          </div>
                        );
                        const val = data.avg;
                        const isPositive = val > 0;
                        const isNeutral = Math.abs(val) < 0.05;
                        const barWidth = Math.min(Math.abs(val) * 40, 100);
                        return (
                          <div key={cat.key} className="rounded-lg bg-muted/20 px-3 py-2.5" data-testid={`sg-${cat.key}`}>
                            <div className="flex items-center gap-3 mb-1.5">
                              <span className="text-sm w-6 text-center">{cat.icon}</span>
                              <span className="text-xs font-bold text-foreground flex-1">{cat.label}</span>
                              <span className={`text-sm font-black ${isNeutral ? 'text-muted-foreground' : isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
                                {isPositive ? '+' : ''}{val.toFixed(2)}
                              </span>
                              <span className="text-[9px] text-muted-foreground">{data.count} holes</span>
                            </div>
                            <div className="flex items-center h-2 ml-9">
                              <div className="flex-1 flex items-center">
                                <div className="w-full h-2 bg-muted/50 rounded-full overflow-hidden relative">
                                  <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border" />
                                  {isPositive ? (
                                    <div
                                      className="absolute left-1/2 top-0 bottom-0 bg-emerald-500/70 rounded-r-full transition-all duration-500"
                                      style={{ width: `${barWidth / 2}%` }}
                                    />
                                  ) : (
                                    <div
                                      className="absolute top-0 bottom-0 bg-red-500/70 rounded-l-full transition-all duration-500"
                                      style={{ width: `${barWidth / 2}%`, right: '50%' }}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Total */}
                    {strokesGained.strokes_gained?.total?.avg !== null && (
                      <div className="border-t border-border/30 pt-3 flex items-center justify-between">
                        <span className="text-xs font-bold text-foreground">Total Strokes Gained</span>
                        <span className={`text-lg font-black ${strokesGained.strokes_gained.total.avg > 0 ? 'text-emerald-500' : strokesGained.strokes_gained.total.avg < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                          {strokesGained.strokes_gained.total.avg > 0 ? '+' : ''}{strokesGained.strokes_gained.total.avg.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground/60 text-center">
                      Positive = gaining strokes vs baseline · Negative = losing strokes
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Round History</h3>
            {history.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No rounds yet</p> : (
              <div className="space-y-2">
                {history.map(r => {
                  const s = r.summary || {};
                  const tp = s.score_to_par;
                  const tpStr = tp === 0 ? 'E' : tp > 0 ? `+${tp}` : `${tp}`;
                  return (
                    <button key={r.id} onClick={() => onViewRound(r)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all text-left" data-testid={`history-round-${r.id}`}>
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex flex-col items-center justify-center">
                        <span className="text-lg font-bold text-primary">{s.total_strokes}</span>
                        <span className={`text-[9px] font-semibold ${tp < 0 ? 'text-red-500' : tp === 0 ? 'text-muted-foreground' : 'text-blue-500'}`}>{tpStr}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-foreground truncate">{r.course_name}</h4>
                        <p className="text-xs text-muted-foreground">{r.tee_name} · {s.holes_played}h · {r.completed_at ? new Date(r.completed_at).toLocaleDateString() : ''}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
